// Single source of truth for "is this Registration Packet step filled?" /
// "is the whole packet complete?" - previously answered independently (and
// inconsistently) in user-profile.js and header.js, plus a third, stale-flag
// based answer in since-deleted dead code. See the "Registration Packet —
// Structural Fixes" plan for the full history.
//
// Labels/copy are deliberately NOT part of this module - header.js and
// user-profile.js already show different visible strings for the same steps
// today, and unifying that would be a copy change, not a structural one.

// Child Registration is the one step with a documented second "filled"
// status: ChildRegistrationListComp.js's own list badge already treats
// status 1 OR 3 as "Active" (with no comment explaining what 3 means). No
// other step shows any evidence of a status-3 concept anywhere in the
// codebase, so this predicate is intentionally Child-Registration-specific.
export function isChildRegistrationRecordFilled(record) {
   return record?.status === 1 || record?.status === 3;
}

// Every other step (Allergy / Lunch / Peanut / Emergency Contact) only has
// one "submitted" status.
export function isStandardStepRecordFilled(record) {
   return record?.status === 1;
}

export function isChildRegistrationFilled(childRegistrationData) {
   return Array.isArray(childRegistrationData) && childRegistrationData.some(isChildRegistrationRecordFilled);
}

// Handles both shapes seen across the app: an array of records (Child
// Registration, Emergency Contacts - real records with a reliable numeric
// status) or a single object embedded directly on the profile
// (allergy_action_plan / lunch_plan_waiver / no_peanut_policy). That
// embedded object does NOT reliably carry the same status === 1 the list
// records do (missing entirely, or returned as a "1" string) - requiring
// predicate(value) against it treated an already-filled step as still
// incomplete every time that happened. Every other place in the app that
// reads this same embedded field (friend-profile.js's document cards,
// this file's own "Documents" tab, and ChildRegistrationFormComp.js's
// firstEmbeddedRecord, which hit this exact bug first) already treats its
// mere presence as a non-empty object as "filled", with no status
// involved - so the array branch (real records) still applies `predicate`,
// but the single-object branch no longer does.
export function isEmbeddedStepFilled(value, predicate = isStandardStepRecordFilled) {
   if (Array.isArray(value)) return value.some(predicate);
   if (value && typeof value === 'object') return Object.keys(value).length > 0;
   return false;
}

// For the profile tab's per-step Form-vs-List decision, which needs the
// actual filtered records, not just a boolean. Same status-vs-presence
// split as isEmbeddedStepFilled above, and for the same reason.
export function filterActiveRecords(value, predicate = isStandardStepRecordFilled) {
   if (Array.isArray(value)) return value.filter(predicate);
   if (value && typeof value === 'object' && Object.keys(value).length > 0) return [value];
   return [];
}

// The Registration Packet tab's red "incomplete" dot is deliberately scoped
// to Child Registration alone (a parent who's already done the core
// registration shouldn't keep seeing an "outstanding" indicator just because
// the three optional forms are still open) - relocated here unchanged.
export function isRegistrationPacketTabIncomplete(childRegistrationData) {
   return !isChildRegistrationFilled(childRegistrationData);
}

// The full 5-step packet, shared by the in-tab "Registration Incomplete!"
// banner (user-profile.js) and the header banner (header.js). `profile` is
// the effective child/user object (displayProfile/regUser in
// user-profile.js, registrationBannerProfile in header.js).
export function getRegistrationPacketSteps({ childRegistrationData, profile, emergencyContactsData }) {
   return [
      { key: 'childRegistration', filled: isChildRegistrationFilled(childRegistrationData) },
      { key: 'allergyActionPlan', filled: isEmbeddedStepFilled(profile?.allergy_action_plan) },
      { key: 'lunchPlanWaiver', filled: isEmbeddedStepFilled(profile?.lunch_plan_waiver) },
      { key: 'noPeanutPolicy', filled: isEmbeddedStepFilled(profile?.no_peanut_policy) },
      { key: 'emergencyContact', filled: isEmbeddedStepFilled(emergencyContactsData) },
   ];
}

export function isRegistrationPacketComplete(steps) {
   return steps.every((step) => step.filled);
}

export function getIncompleteStepKeys(steps) {
   return steps.filter((step) => !step.filled).map((step) => step.key);
}
